import { Badge, Button, Card, Input, Spinner } from '@fc/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Filter, Plus, Users } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import { createList, listLists, type ListSummary } from '../../lib/lists-api'

const kindVariant: Record<string, 'success' | 'neutral' | 'gold'> = {
  dynamic: 'success',
  static: 'neutral',
}

export function ListsList() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['lists'],
    queryFn: () => listLists(),
  })

  const create = useMutation({
    mutationFn: () => createList({ name: 'Untitled segment', kind: 'dynamic' }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['lists'] })
      navigate(`/app/lists/${created.id}`)
    },
  })

  const filtered: ListSummary[] = (data ?? []).filter((l) =>
    l.name.toLowerCase().includes(search.toLowerCase()),
  )

  return (
    <div className="space-y-6">
      <PageHeader
        title="Lists"
        description="Saved demographic segments that update as your people change."
        actions={
          <Button onClick={() => create.mutate()} disabled={create.isPending}>
            {create.isPending ? <Spinner /> : <Plus />}
            New segment
          </Button>
        }
      />

      <div className="flex items-center gap-3">
        <Input
          placeholder="Search segments…"
          className="max-w-sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search segments"
        />
      </div>

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-muted">
            <Spinner /> Loading segments…
          </div>
        ) : isError ? (
          <div className="space-y-2 py-20 text-center">
            <p className="text-sm text-danger">Couldn’t load segments.</p>
            <p className="text-xs text-dim">
              {error instanceof Error ? error.message : 'Is the API running on :3000?'}
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-20 text-center">
            <div className="grid size-12 place-items-center rounded-full bg-card text-secondary">
              <Filter className="size-6" />
            </div>
            <p className="max-w-sm text-sm text-muted">
              {search
                ? 'No segments match your search.'
                : 'No segments yet. Create one to group people by membership, tags, dates, and more.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-dim">
                  <th className="px-5 py-3 font-semibold">Name</th>
                  <th className="px-5 py-3 font-semibold">Type</th>
                  <th className="px-5 py-3 font-semibold">Members</th>
                  <th className="px-5 py-3 font-semibold">Last evaluated</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr
                    key={l.id}
                    tabIndex={0}
                    role="link"
                    onClick={() => navigate(`/app/lists/${l.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        navigate(`/app/lists/${l.id}`)
                      }
                    }}
                    className="cursor-pointer border-b border-border-soft outline-none transition-colors last:border-0 hover:bg-card-hover focus-visible:bg-card-hover"
                  >
                    <td className="px-5 py-3">
                      <div className="font-medium">{l.name}</div>
                      {l.description && (
                        <div className="truncate text-xs text-dim">{l.description}</div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <Badge variant={kindVariant[l.kind] ?? 'neutral'} className="capitalize">
                        {l.kind}
                      </Badge>
                    </td>
                    <td className="px-5 py-3 text-muted">
                      {l.cachedCount === null ? (
                        <span className="text-dim">—</span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5">
                          <Users className="size-3.5 text-dim" />
                          {l.cachedCount.toLocaleString()}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-muted">
                      {l.lastEvaluatedAt ? new Date(l.lastEvaluatedAt).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
